import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { searchAPI } from '../../services/api';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  maxTags?: number;
  placeholder?: string;
}

const TagSelector: React.FC<TagSelectorProps> = ({ 
  selectedTags, 
  onChange, 
  maxTags = 10,
  placeholder = "Select tags..." 
}) => {
  const [categories, setCategories] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await searchAPI.getPredefinedTags();
        if (response.data.success) {
          setCategories(response.data.data);
          // Expand the first category by default
          const firstCategory = Object.keys(response.data.data)[0];
          if (firstCategory) setExpandedCategory(firstCategory);
        }
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onChange(selectedTags.filter(t => t !== tag));
    } else {
      if (selectedTags.length < maxTags) {
        onChange([...selectedTags, tag]);
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  // Filter tags based on search term
  const filteredCategories = Object.entries(categories).reduce((acc, [category, tags]) => {
    const filteredTags = tags.filter(tag => 
      tag.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filteredTags.length > 0) {
      acc[category] = filteredTags;
    }
    return acc;
  }, {} as Record<string, string[]>);

  return (
    <div className="w-full" ref={dropdownRef}>
      {/* Selected Tags Display */}
      <div className="flex flex-wrap gap-2 mb-3">
        {selectedTags.map(tag => (
          <span 
            key={tag} 
            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-black dark:bg-white text-white dark:text-black"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="ml-1.5 h-3.5 w-3.5 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
        {selectedTags.length === 0 && (
            <span className="text-sm text-gray-400 dark:text-gray-500 italic">No tags selected</span>
        )}
      </div>

      {/* Search/Dropdown Trigger */}
      <div className="relative">
        <div className="relative">
            <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={placeholder}
            className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 focus:border-black dark:focus:border-white transition-all text-sm text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setShowDropdown(!showDropdown)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
            >
                {showDropdown ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </button>
        </div>

        {/* Dropdown Content */}
        {showDropdown && (
          <div className="absolute z-[60] w-full mt-2 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 max-h-60 overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">Loading tags...</div>
            ) : Object.keys(filteredCategories).length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">No matching tags found</div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-800">
                {Object.entries(filteredCategories)
                  .sort(([a], [b]) => {
                    const priority = ['Photography', 'Videography', 'Modeling'];
                    const indexA = priority.indexOf(a);
                    const indexB = priority.indexOf(b);
                    
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                  })
                  .map(([category, tags]) => (
                  <div key={category} className="p-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      {category}
                      {searchTerm ? null : (
                        expandedCategory === category ? 
                        <ChevronUpIcon className="w-3 h-3" /> : 
                        <ChevronDownIcon className="w-3 h-3" />
                      )}
                    </button>
                    
                    {(expandedCategory === category || searchTerm) && (
                      <div className="flex flex-wrap gap-1.5 mt-2 px-2 pb-2">
                        {tags.map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => handleTagClick(tag)}
                            disabled={selectedTags.includes(tag)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                              selectedTags.includes(tag)
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="mt-2 flex justify-between items-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
            {selectedTags.length}/{maxTags} tags selected
        </p>
        {showDropdown && (
            <button 
                type="button" 
                onClick={() => setShowDropdown(false)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
                Done
            </button>
        )}
      </div>
    </div>
  );
};

export default TagSelector;
